import useEmblaCarousel from 'embla-carousel-react';
import { ReactNode, useCallback, useEffect, useState } from 'react';

import ChevronIcon from '~/assets/icons/Chevron';

const LOOP_SAFETY_MARGIN = 4;
const MAX_CAROUSEL_WIDTH = 2040;

type Props<T> = {
  autoSlideDuration: number;
  fade?: number;
  getSlideKey: (item: T, index: number) => string;
  hasEqualGaps?: boolean;
  renderSlideContent: (item: T, index: number) => ReactNode;
  scaleDown?: number;
  slides: T[];
};

export default function HighlightCarousel<T>({
  autoSlideDuration,
  fade = 1,
  getSlideKey,
  hasEqualGaps = false,
  renderSlideContent,
  scaleDown = 0,
  slides
}: Props<T>) {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: 'center',
    dragFree: false,
    loop: true,
    slidesToScroll: 1
  });
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [maxWidth, setMaxWidth] = useState<number>();

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;

    const selectedSlide = emblaApi.selectedScrollSnap();

    setCurrentSlideIndex(selectedSlide);
  }, [emblaApi]);

  const onUnfocusedClick = useCallback(
    (index: number) => {
      if (!emblaApi) return;

      setCurrentSlideIndex(index);
      emblaApi.scrollTo(index);
    },
    [emblaApi]
  );

  useEffect(() => {
    if (!emblaApi) return;

    const timer = setTimeout(() => {
      emblaApi.scrollNext();
    }, autoSlideDuration);

    return () => {
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emblaApi, currentSlideIndex]);

  useEffect(() => {
    if (!emblaApi) return;
    emblaApi.on('select', onSelect);

    return () => {
      emblaApi.off('select', onSelect);
    };
  }, [emblaApi, onSelect]);

  useEffect(() => {
    if (!emblaApi) return;

    const capWidth = () =>
      setMaxWidth(
        Math.min(
          MAX_CAROUSEL_WIDTH,
          getLoopableWidth(emblaApi.slideNodes()) ?? MAX_CAROUSEL_WIDTH
        )
      );

    capWidth();
    emblaApi.on('reInit', capWidth);

    return () => {
      emblaApi.off('reInit', capWidth);
    };
  }, [emblaApi]);

  return (
    <div className="relative mx-auto" style={{ maxWidth }}>
      <div className="absolute inset-y-1/2 z-10 flex w-full items-center justify-between px-4">
        <button
          onClick={scrollPrev}
          aria-label="Previous slide"
          className="rotate-90 rounded-full bg-black/20 p-2 hover:bg-black/40"
        >
          <ChevronIcon height="16" width="16" stroke="white" />
        </button>
        <button
          onClick={scrollNext}
          aria-label="Next slide"
          className="rotate-270 rounded-full bg-black/20 p-2 hover:bg-black/40"
        >
          <ChevronIcon height="16" width="16" stroke="white" />
        </button>
      </div>

      <div className="overflow-hidden py-4" ref={emblaRef}>
        <div className="flex items-center">
          {slides.map((item, index) => {
            const isCurrent = index === currentSlideIndex;
            const slide = (
              <Slide
                fade={fade}
                isCurrent={isCurrent}
                onUnfocusedClick={() => onUnfocusedClick(index)}
                scaleDown={scaleDown}
              >
                {renderSlideContent(item, index)}
              </Slide>
            );

            if (!hasEqualGaps) {
              return (
                <div key={getSlideKey(item, index)} className="flex flex-none">
                  {slide}
                </div>
              );
            }

            const total = slides.length;
            let distance = index - currentSlideIndex;
            if (distance > total / 2) distance -= total;
            if (distance < -total / 2) distance += total;
            const direction = Math.sign(distance);

            return (
              <div
                key={getSlideKey(item, index)}
                className="flex flex-none basis-[70vw] justify-center md:basis-[360px]"
              >
                <div
                  className="mx-[-50vw] transition-transform duration-300 ease-in-out md:mx-[-32px]"
                  style={{ transform: `translateX(${direction * 10}%)` }}
                >
                  {slide}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function getLoopableWidth(slideNodes: HTMLElement[]) {
  if (slideNodes.length < 2) return undefined;

  const slotWidths = slideNodes.map((node) => node.offsetWidth);
  const widestCard = Math.max(...slideNodes.map(getCardWidth));
  const loopableWidth =
    slotWidths.reduce((total, width) => total + width, 0) -
    Math.max(...slotWidths) -
    LOOP_SAFETY_MARGIN;

  return loopableWidth > 0 && loopableWidth >= widestCard
    ? loopableWidth
    : undefined;
}

function getCardWidth(slideNode: HTMLElement) {
  const card = slideNode.firstElementChild;

  return card instanceof HTMLElement ? card.offsetWidth : slideNode.offsetWidth;
}

type SlideProps = {
  children: ReactNode;
  fade?: number;
  isCurrent: boolean;
  onUnfocusedClick: () => void;
  scaleDown: number;
};

function Slide({
  children,
  fade = 1,
  isCurrent,
  onUnfocusedClick,
  scaleDown
}: SlideProps) {
  return (
    <div className="relative">
      <div
        aria-hidden={!isCurrent}
        className={`transition duration-300 ease-in-out ${isCurrent ? 'shadow-[0px_4px_16px_0px_rgba(0,0,0,0.16)]' : 'pointer-events-none'}`}
        style={{
          opacity: isCurrent ? 1 : fade,
          transform: `scale(${isCurrent ? 1 : 1 - scaleDown})`
        }}
        {...(!isCurrent && { inert: '' })}
      >
        {children}
      </div>
      {!isCurrent && (
        <button
          aria-label="Focus slide"
          className="absolute inset-0 z-10 cursor-pointer"
          onClick={onUnfocusedClick}
          type="button"
        />
      )}
    </div>
  );
}
